import type {
    AgencyJobLogContentItem,
    AgencyJobLogMessage,
    AgencyJobLogs,
    AgencyLogsArchiveEntry,
    AgencyLogsArchiveTreeNode,
} from './types';

export type AgencyLogEntryCategory = 'prompt' | 'assistant' | 'tool' | 'diff' | 'unknown';

export interface AgencyLogDisplayEntry {
    key: string;
    category: AgencyLogEntryCategory;
    role: string;
    item: AgencyJobLogContentItem;
    result?: AgencyJobLogContentItem;
    completion: boolean;
}

export interface AgencyLogDisplayGroup {
    key: string;
    label: string;
    entries: AgencyLogDisplayEntry[];
    completion: boolean;
}

export interface ParsedJsonLine {
    index: number;
    raw: string;
    value?: unknown;
    error?: string;
}

export const LOG_ENTRY_FILTER_OPTIONS: Array<{ key: AgencyLogEntryCategory; text: string }> = [
    { key: 'prompt', text: 'Prompts' },
    { key: 'assistant', text: 'Assistant messages' },
    { key: 'tool', text: 'Tool calls and results' },
    { key: 'diff', text: 'Tool diffs' },
    { key: 'unknown', text: 'Unknown entries' },
];

export function isAgencyJobLogs(value: unknown): value is AgencyJobLogs {
    return typeof value === 'object'
        && value !== null
        && Array.isArray((value as { chatHistory?: unknown }).chatHistory);
}

export function getMessageRole(message: AgencyJobLogMessage) {
    const role = message.role?.label ?? message.authorName;
    return typeof role === 'string' && role.trim() ? role.trim().toLowerCase() : 'unknown';
}

export function getItemType(item: AgencyJobLogContentItem) {
    return typeof item.$type === 'string' && item.$type.trim() ? item.$type.trim() : 'Unknown';
}

export function getItemText(item: AgencyJobLogContentItem) {
    return typeof item.text === 'string' ? item.text : undefined;
}

export function buildLogDisplayGroups(logs: AgencyJobLogs): AgencyLogDisplayGroup[] {
    const messages = logs.chatHistory ?? [];
    const resultByCallId = new Map<string, AgencyJobLogContentItem>();

    messages.forEach(message => {
        message.items?.forEach(item => {
            if (getItemType(item) === 'FunctionResultContent' && typeof item.callId === 'string') {
                resultByCallId.set(item.callId, item);
            }
        });
    });
    const pairedCallIds = new Set<string>();
    messages.forEach(message => {
        message.items?.forEach(item => {
            if (getItemType(item) === 'FunctionCallContent'
                && typeof item.id === 'string'
                && resultByCallId.has(item.id)) {
                pairedCallIds.add(item.id);
            }
        });
    });

    const rawEntries: AgencyLogDisplayEntry[] = [];
    messages.forEach((message, messageIndex) => {
        const role = getMessageRole(message);
        const items = Array.isArray(message.items) && message.items.length > 0
            ? message.items
            : [{ $type: 'UnknownMessage', message }];

        items.forEach((item, itemIndex) => {
            const type = getItemType(item);
            if (type === 'TextContent' && !(getItemText(item)?.trim())) {
                return;
            }

            if (type === 'FunctionResultContent' && typeof item.callId === 'string' && pairedCallIds.has(item.callId)) {
                return;
            }

            const result = type === 'FunctionCallContent' && typeof item.id === 'string'
                ? resultByCallId.get(item.id)
                : undefined;

            rawEntries.push({
                key: `${messageIndex}-${itemIndex}`,
                category: getEntryCategory(role, type),
                role,
                item,
                result,
                completion: false,
            });
        });
    });

    let finalAssistantIndex = -1;
    for (let index = rawEntries.length - 1; index >= 0; index -= 1) {
        const entry = rawEntries[index];
        if (entry.category === 'assistant'
            && getItemType(entry.item) === 'TextContent'
            && !!getItemText(entry.item)?.trim()) {
            finalAssistantIndex = index;
            break;
        }
    }
    if (finalAssistantIndex >= 0) {
        rawEntries[finalAssistantIndex] = { ...rawEntries[finalAssistantIndex], completion: true };
    }

    const promptEntries = rawEntries.filter(entry => entry.category === 'prompt');
    const sessionEntries = rawEntries.filter(entry => entry.category !== 'prompt');
    const hasProgressBoundary = sessionEntries.some(entry => isProgressEntry(entry.item));
    const groups: AgencyLogDisplayGroup[] = [];

    if (promptEntries.length > 0) {
        groups.push({
            key: 'prompts',
            label: 'System and user prompts',
            entries: promptEntries,
            completion: promptEntries.some(entry => entry.completion),
        });
    }

    if (!hasProgressBoundary) {
        if (sessionEntries.length > 0) {
            groups.push({
                key: 'transcript',
                label: 'Session transcript',
                entries: sessionEntries,
                completion: sessionEntries.some(entry => entry.completion),
            });
        }
        return groups;
    }

    let currentEntries: AgencyLogDisplayEntry[] = [];
    let groupIndex = 0;
    sessionEntries.forEach(entry => {
        currentEntries.push(entry);
        if (!isProgressEntry(entry.item)) {
            return;
        }

        groups.push({
            key: `progress-${groupIndex++}`,
            label: getProgressLabel(entry.item),
            entries: currentEntries,
            completion: currentEntries.some(candidate => candidate.completion),
        });
        currentEntries = [];
    });

    if (currentEntries.length > 0) {
        groups.push({
            key: 'conclusion',
            label: 'Report conclusion',
            entries: currentEntries,
            completion: currentEntries.some(entry => entry.completion),
        });
    }

    return groups;
}

export function buildArchiveTree(entries: AgencyLogsArchiveEntry[]): AgencyLogsArchiveTreeNode[] {
    const roots: AgencyLogsArchiveTreeNode[] = [];

    entries.forEach(entry => {
        const segments = entry.path.split('/');
        let level = roots;
        let accumulatedPath = '';
        segments.forEach((segment, index) => {
            accumulatedPath = accumulatedPath ? `${accumulatedPath}/${segment}` : segment;
            const isFile = index === segments.length - 1;
            let node = level.find(candidate => candidate.name === segment && candidate.isFile === isFile);
            if (!node) {
                node = {
                    name: segment,
                    path: accumulatedPath,
                    isFile,
                    entry: isFile ? entry : undefined,
                    children: [],
                };
                level.push(node);
            }
            level = node.children;
        });
    });

    const sortNodes = (nodes: AgencyLogsArchiveTreeNode[]) => {
        nodes.sort((left, right) =>
            Number(left.isFile) - Number(right.isFile)
            || left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
            || left.name.localeCompare(right.name),
        );
        nodes.forEach(node => sortNodes(node.children));
    };
    sortNodes(roots);
    return roots;
}

export function parseJsonLines(content: string): ParsedJsonLine[] {
    return content.split(/\r?\n/).flatMap((raw, index): ParsedJsonLine[] => {
        if (!raw.trim()) {
            return [];
        }

        try {
            return [{ index, raw, value: JSON.parse(raw) as unknown }];
        } catch (error) {
            return [{
                index,
                raw,
                error: error instanceof Error ? error.message : 'Invalid JSON',
            }];
        }
    });
}

export function findLastShutdownRecord(lines: ParsedJsonLine[]) {
    for (let index = lines.length - 1; index >= 0; index -= 1) {
        const value = lines[index].value;
        if (typeof value === 'object'
            && value !== null
            && (value as { type?: unknown }).type === 'session.shutdown') {
            return index;
        }
    }
    return -1;
}

function getEntryCategory(role: string, itemType: string): AgencyLogEntryCategory {
    if ((role === 'user' || role === 'system') && itemType === 'TextContent') {
        return 'prompt';
    }
    if (role === 'assistant' && itemType === 'TextContent') {
        return 'assistant';
    }
    if (itemType === 'FunctionCallContent' || itemType === 'FunctionResultContent') {
        return 'tool';
    }
    return 'unknown';
}

function isProgressEntry(item: AgencyJobLogContentItem) {
    return getItemType(item) === 'FunctionCallContent'
        && typeof item.functionName === 'string'
        && ['report_progress', 'report_intent', 'asa-report_progress'].includes(item.functionName);
}

function getProgressLabel(item: AgencyJobLogContentItem) {
    const args = typeof item.arguments === 'object' && item.arguments !== null
        ? item.arguments as Record<string, unknown>
        : {};
    const label = item.functionName === 'report_intent'
        ? args.intent
        : item.functionName === 'asa-report_progress'
            ? args.commit_message
            : args.commitMessage;
    return typeof label === 'string' && label.trim() ? label.trim() : String(item.functionName);
}
